import { ColumnBlockNode as ColumnBlockSchema } from '@seen/core/tiptap/schema'
import { Fragment, type Node as ProseMirrorNode, Slice } from '@tiptap/pm/model'
import {
  type EditorState,
  Plugin,
  Selection,
  type Transaction,
} from '@tiptap/pm/state'
import { type RawCommands, ReactNodeViewRenderer } from '@tiptap/react'
import { ColumnBlockView } from '#src/components/rich-text-editor/column/ColumnBlockView.tsx'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    columnBlock: {
      setColumns: (count: number) => ReturnType
      unsetColumns: () => ReturnType
    }
  }
}

interface ColumnBlockMatch {
  node: ProseMirrorNode
  pos: number
}

/**
 * Find all columnBlock nodes that overlap with the current selection.
 * @param state - The editor state to search within
 * @returns Matches sorted by position (ascending)
 */
function findColumnBlocksInSelection(state: EditorState): ColumnBlockMatch[] {
  const { $from, $to } = state.selection
  const matches: ColumnBlockMatch[] = []
  const seenPositions = new Set<number>()

  // Helper to add a match if not already seen
  const addMatch = (node: ProseMirrorNode, pos: number) => {
    if (!seenPositions.has(pos)) {
      seenPositions.add(pos)
      matches.push({ node, pos })
    }
  }

  // Check ancestors of $from
  for (let depth = $from.depth; depth > 0; depth--) {
    if ($from.node(depth).type.name === 'columnBlock') {
      addMatch($from.node(depth), $from.before(depth))
    }
  }

  // Check ancestors of $to
  for (let depth = $to.depth; depth > 0; depth--) {
    if ($to.node(depth).type.name === 'columnBlock') {
      addMatch($to.node(depth), $to.before(depth))
    }
  }

  // Check nodes within selection range
  state.doc.nodesBetween($from.pos, $to.pos, (node, pos) => {
    if (node.type.name === 'columnBlock') {
      addMatch(node, pos)
      return false // don't descend into columnBlock
    }
    return true
  })

  // Sort by position ascending
  return matches.sort((a, b) => a.pos - b.pos)
}

function flattenColumnBlock(node: ProseMirrorNode): ProseMirrorNode[] {
  const content: ProseMirrorNode[] = []
  node.forEach((column: ProseMirrorNode) => {
    column.forEach((child: ProseMirrorNode) => {
      content.push(child)
    })
  })
  return content
}

function selectionOverlapsColumn(state: EditorState): boolean {
  return findColumnBlocksInSelection(state).length > 0
}

export const ColumnBlockNode = ColumnBlockSchema.extend({
  addNodeView() {
    return ReactNodeViewRenderer(ColumnBlockView)
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          transformPasted: (slice, view) => {
            const { state } = view
            const { $from } = state.selection

            // Check if we're pasting INTO a column
            let isInsideColumn = false
            for (let depth = $from.depth; depth > 0; depth--) {
              if ($from.node(depth).type.name === 'column') {
                isInsideColumn = true
                break
              }
            }

            if (!isInsideColumn) {
              // Not inside a column - preserve structure as-is
              return slice
            }

            // Inside a column - flatten any columnBlock nodes in the pasted content
            const flattenedContent: ProseMirrorNode[] = []

            slice.content.forEach((node) => {
              if (node.type.name === 'columnBlock') {
                // Extract children from each column
                node.forEach((column) => {
                  column.forEach((child) => {
                    flattenedContent.push(child)
                  })
                })
              } else {
                flattenedContent.push(node)
              }
            })

            // Use 0, 0 for a closed slice since we've flattened the nesting structure
            return new Slice(Fragment.from(flattenedContent), 0, 0)
          },
        },
      }),
    ]
  },

  addCommands() {
    return {
      setColumns:
        (count: number) =>
        ({
          state,
          tr,
          dispatch,
        }: {
          state: EditorState
          tr: Transaction
          dispatch?: (tr: Transaction) => void
        }) => {
          const clampedCount = Math.max(2, Math.min(4, count))
          const { selection } = state
          const { $from, $to } = selection

          // Prevent nested columns
          if (selectionOverlapsColumn(state)) {
            return false
          }

          // Use resolved positions to get block boundaries at depth 1 (document children)
          // Handle edge case when selection is at doc level (depth 0)
          const fromDepth = Math.min($from.depth, 1)
          const toDepth = Math.min($to.depth, 1)
          const startBlockPos = fromDepth > 0 ? $from.start(1) - 1 : 0
          const endBlockPos =
            toDepth > 0 ? $to.end(1) + 1 : state.doc.content.size

          // Collect all top-level blocks in the range
          const blocks: { node: ProseMirrorNode; pos: number }[] = []
          state.doc.nodesBetween(
            startBlockPos,
            endBlockPos,
            (node, pos, parent) => {
              // Only collect direct children of the document
              if (parent === state.doc && node.isBlock) {
                blocks.push({ node, pos })
                return false // Don't descend into this block
              }
              return true
            }
          )

          if (blocks.length === 0) {
            return false
          }

          // Build columns: first with selected content, rest empty
          const columnType = state.schema.nodes.column
          const paragraphType = state.schema.nodes.paragraph
          const columnBlockType = state.schema.nodes.columnBlock

          const selectedContent = blocks.map((b) => b.node)
          const columns = Array.from({ length: clampedCount }, (_, i) => {
            if (i === 0) {
              // First column gets the selected blocks
              return columnType.create(null, selectedContent)
            }
            // Other columns get empty paragraph
            return columnType.create(null, paragraphType.create())
          })

          if (dispatch) {
            const columnBlock = columnBlockType.create(
              { layout: 'equal' },
              columns
            )
            // Replace from first block to last block
            const firstPos = blocks[0].pos
            const lastBlock = blocks[blocks.length - 1]
            const lastPos = lastBlock.pos + lastBlock.node.nodeSize
            tr.replaceWith(firstPos, lastPos, columnBlock)

            // Place cursor in the second (empty) column so user can start typing there.
            // Structure: columnBlock > column1 (with content) > column2 (empty paragraph)
            // Position: firstPos + 1 (into columnBlock) + firstColumn.nodeSize (skip first column)
            // scrollIntoView() ensures cursor is visible and helps maintain focus when
            // command is triggered from UI buttons (which steal focus on click).
            const firstColumnSize = columnBlock.firstChild?.nodeSize ?? 0
            const secondColumnStart = firstPos + 1 + firstColumnSize
            const $start = tr.doc.resolve(secondColumnStart + 1)
            const sel = Selection.findFrom($start, 1, true)
            if (sel) {
              tr.setSelection(sel).scrollIntoView()
            }

            dispatch(tr)
          }
          return true
        },

      unsetColumns:
        () =>
        ({
          state,
          tr,
          dispatch,
        }: {
          state: EditorState
          tr: Transaction
          dispatch?: (tr: Transaction) => void
        }) => {
          const matches = findColumnBlocksInSelection(state)

          if (matches.length === 0) {
            return false
          }

          if (dispatch) {
            // Process in reverse order to avoid position shifts
            for (let i = matches.length - 1; i >= 0; i--) {
              const { node, pos } = matches[i]
              const content = flattenColumnBlock(node)
              tr.replaceWith(
                pos,
                pos + node.nodeSize,
                content.length > 0
                  ? content
                  : state.schema.nodes.paragraph.create()
              )
            }
            dispatch(tr)
          }
          return true
        },
    } as Partial<RawCommands>
  },
})
